// BAFA API helpers. Wraps the same endpoints the legacy app talks to (under
// /api/report, /api/questionnaire, /api/data) so the React wizard can read &
// write the agent's output files.

import { fetchWithSlugFallback } from "../../shared/slug";

export interface CompanyInfo {
  firmenname?: string;
  branche?: string;
  inhaber?: string;
  standort?: string;
  [k: string]: unknown;
}

export type Questionnaire = Record<string, unknown>;

export async function fetchCompanyInfo(
  slugs: (string | null | undefined)[],
): Promise<{ data: CompanyInfo | null; slug: string | null }> {
  const { response, slug } = await fetchWithSlugFallback(
    slugs,
    (s) => "/api/data/" + s + "/company_info.json",
    3,
    1500,
  );
  if (!response) return { data: null, slug: null };
  const data = (await response.json()) as CompanyInfo;
  return { data, slug };
}

export async function fetchQuestionnaire(
  slugs: (string | null | undefined)[],
): Promise<{ data: Questionnaire | null; slug: string | null }> {
  const { response, slug } = await fetchWithSlugFallback(
    slugs,
    (s) => "/api/questionnaire/" + s,
    1,
  );
  if (!response) return { data: null, slug: null };
  const data = (await response.json()) as Questionnaire;
  return { data, slug };
}

export async function saveQuestionnaire(
  slug: string,
  data: Questionnaire,
): Promise<void> {
  const r = await fetch("/api/questionnaire/" + encodeURIComponent(slug), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    credentials: "include",
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(err.error || r.statusText || "Speichern fehlgeschlagen");
  }
}

export async function fetchReport(
  slugs: (string | null | undefined)[],
  retries = 3,
  delayMs = 2000,
): Promise<{ content: string | null; slug: string | null }> {
  const { response, slug } = await fetchWithSlugFallback(
    slugs,
    (s) => "/api/report/" + s,
    retries,
    delayMs,
  );
  if (!response) return { content: null, slug: null };
  const data = (await response.json()) as { content?: string };
  return { content: data.content ?? "", slug };
}

export async function saveReport(slug: string, content: string): Promise<void> {
  const r = await fetch("/api/report/" + encodeURIComponent(slug), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
    credentials: "include",
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(err.error || r.statusText || "Speichern fehlgeschlagen");
  }
}

export async function downloadGeneratedFile(
  slugs: (string | null | undefined)[],
  endpoint: "pdf" | "docx",
  resource: "report" | "questionnaire",
  filename: string,
): Promise<{ slug: string | null; ok: boolean; error?: string }> {
  const { response, slug } = await fetchWithSlugFallback(
    slugs,
    (s) => `/api/${resource}/${s}/${endpoint}`,
    1,
  );
  if (!response) return { slug: null, ok: false, error: "Datei nicht gefunden" };
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    return { slug, ok: false, error: err.error ?? response.statusText };
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { slug, ok: true };
}
